import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormControl, FormGroup } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { IApplication } from '../models/application.model';

import { ApplicationService } from '../Services/application.service';
import { ReleaseNoteService } from '../Services/release-note.service';
import { HeaderService } from '../Services/header.service';
import { QuillService } from '../Services/quill.service';
import { ViewEncapsulation } from '@angular/core';
import { ITransferNote } from '../models/release-note.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-note-entry',
  templateUrl: './note-entry.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['../app.component.scss', '../quill.component.css']
})
export class NoteEntryComponent implements OnInit {
  isSet = false;
  approved = false;
  messageAction = 'updated';
  publishToggle = 'Publish Release Notes';
  cancelledChange = false;
  noteID: number;

  xfer: ITransferNote;
  xferNoteSub: Subscription;

  appList: IApplication[];
  notesForm: FormGroup;
  formOriginal: any;

  get ApplicationId() { return this.notesForm.get('ApplicationId'); }
  get VersionNum() { return this.notesForm.get('VersionNum'); }
  get RDate() { return this.notesForm.get('RDate'); }
  get IsHotfix() { return this.notesForm.get('IsHotfix'); }
  get IsApproved() { return this.notesForm.get('IsApproved'); }

  constructor(private applicationService: ApplicationService, private releaseNoteService: ReleaseNoteService,
              private headerService: HeaderService, private quillService: QuillService, private datePipe: DatePipe,
              protected Sanitizer: DomSanitizer, private formBuilder: FormBuilder, private router: Router) { }

  dateValidator(control: FormControl): { [key: string]: boolean } | null {
    if (control.value !== null && typeof control.value === 'object') {
      if (control.value.year && +control.value.year > 1950) {
        return null;
      }
    }
    return { dateError: true };
  }

  ngOnInit(): void {
    this.applicationService.getApplications().subscribe(x => { this.appList = x; });

    this.quillService.quillSetupEntry();
    this.headerService.checkAuth(true);
    this.headerService.setBreadcrumbs(null, null, null, true);

    this.notesForm = this.formBuilder.group({
      ApplicationId: new FormControl('', Validators.required),
      RDate: new FormControl('', [this.dateValidator]),
      ReleaseDate: new FormControl(),
      VersionNum: new FormControl('', [Validators.required, Validators.maxLength(25)]),
      NoteContent: new FormControl('', Validators.required),
      IsHotfix: new FormControl(false),
      IsApproved: new FormControl(false)
    });

    this.xferNoteSub = this.releaseNoteService.transferNote$.subscribe(
      note => {
        this.xfer = note;
      });

    this.releaseNoteService.pingTransferNote();
  }

  ngAfterContentChecked(): void {
    if (this.xfer && this.appList) {
      this.xferNoteSub.unsubscribe();

      const id = this.appList.find(x => x.appName == this.xfer.appName).applicationId;
      this.ApplicationId.patchValue(id);
      const newDate = {
        year: this.xfer.releaseDate.getUTCFullYear(),
        month: (this.xfer.releaseDate.getUTCMonth() + 1),
        day: this.xfer.releaseDate.getUTCDate()
      };
      this.RDate.patchValue(newDate);
      this.VersionNum.patchValue(this.xfer.versionNum);

      this.onChange(null);
      this.xfer = null;
      this.releaseNoteService.setTransferNote(null);
    }
  }

  onChange(event) {
    if (!this.xfer) {
      this.headerService.clearAlerts();
    }
    if (!this.cancelledChange) {
      if (this.isOKForReset()) {
        if (typeof event === 'string') {
          const date = event.replace(/[^0-9]/g, '/');
          this.validateAndCommitDate(date);
        }
        this.setupFormForEntry();
      } else {
        this.cancelledChange = true;
        this.notesForm.patchValue({
          ApplicationId: this.formOriginal.ApplicationId,
          RDate: this.formOriginal.RDate,
          VersionNum: this.formOriginal.VersionNum
        });
      }
    }
    this.cancelledChange = false;
  }

  validateAndCommitDate(date: string) {
    if (date.length === 10 && !isNaN(Date.parse(date))) {
      const d = new Date(Date.parse(date));
      const newDate = {
        year: d.getUTCFullYear(),
        month: (d.getUTCMonth() + 1),
        day: d.getUTCDate()
      };

      this.notesForm.patchValue({ RDate: newDate });
    }
  }

  isOKForReset(): boolean {
    const currentNote = JSON.stringify(this.quillService.quill.getContents())
      + this.notesForm.get('IsHotfix').value.toString();

    if (this.formOriginal) {
      const originalnote = this.formOriginal.NoteContent
        + this.formOriginal.IsHotfix.toString();

      if (currentNote !== originalnote) {
        return confirm('It appears that you have made changes to this entry - if you proceed, these will be lost');
      }
    }
    return true;
  }

  setupFormForEntry() {
    if (this.ApplicationId.valid) {
      const appName = this.appList.find(x => x.applicationId == this.ApplicationId.value).appName;
      this.headerService.setBreadcrumbs(appName, +this.ApplicationId.value, null, true);

      if (this.RDate.valid) {
        const monthYear = this.RDate.value.month.toString() + this.RDate.value.year.toString();
        this.headerService.setBreadcrumbs(appName, +this.ApplicationId.value, monthYear, true);
      }
    }

    if (this.ApplicationId.valid && this.RDate.valid && this.VersionNum.valid) {
      this.setNewOrEdit();
      this.isSet = true;
    } else {
      this.isSet = false;
      this.approved = false;
    }
  }

  setNewOrEdit() {
    const app = +this.ApplicationId.value;
    const dt = [
      this.RDate.value.year,
      this.RDate.value.month,
      this.RDate.value.day
    ];
    const version = this.VersionNum.value;
    this.releaseNoteService.getNotebyDateVersion(app, dt, version).subscribe({
      next: releaseNotes => {
        this.noteID = releaseNotes.data[0] ? releaseNotes.data[0].releaseNoteId : null;
        const delta = (releaseNotes.data[0] ? JSON.parse(releaseNotes.data[0].noteContent) : this.quillService.defaultDelta);
        this.quillService.quill.setContents(delta);

        const hotfix = releaseNotes.data[0] ? releaseNotes.data[0].isHotfix : false;
        const isApproved = releaseNotes.data[0] ? releaseNotes.data[0].isApproved : false;
        if (isApproved) {
          this.quillService.quillDisableEdit();
        } else {
          this.quillService.quillEnableEdit();
        }
        this.ResetOriginalFormValues(hotfix, delta, isApproved);
      },
    });
  }

  onSubmit() {
    if (this.isFormValid()) {
      // datepicker doesn't save value (RDate) as a date, have to convert it and save into ReleaseDate control
      const date = new Date(Date.UTC(this.RDate.value.year, this.RDate.value.month - 1, this.RDate.value.day, 0, 0, 0));
      this.notesForm.patchValue({ ReleaseDate: date });
      this.ResetOriginalFormValues(this.IsHotfix.value, this.quillService.quill.getContents(), this.IsApproved.value);

      if (this.noteID) {
        this.patchExistingNote();
      } else {
        this.postNewNote();
      }
    }
  }

  private ResetOriginalFormValues(hotfix, delta: any, isApproved) {
    this.notesForm.patchValue({ IsHotfix: hotfix, IsApproved: isApproved });
    this.notesForm.get('NoteContent').setValue(JSON.stringify(delta));
    this.formOriginal = this.notesForm.value;
    this.approved = this.IsApproved.value;
    this.publishToggle = this.approved ? 'Unpublish Release Notes' : 'Publish Release Notes';
  }

  onDelete() {
    if (confirm('Please confirm delete')) {
      if (this.isFormValid() && this.noteID) {
        this.removeNote();
      }
    }
  }

  onPublish() {
    if (!this.approved) {
      if (confirm('Please confirm to Save and Publish')) {
        this.messageAction = 'published';
        this.quillService.quillDisableEdit();
        this.publish();
      }
    } else {
      this.messageAction = 'unpublished';
      this.quillService.quillEnableEdit();
      this.publish();
    }
  }

  publish() {
    if (this.isFormValid() && this.noteID) {
      this.notesForm.patchValue({ IsApproved: !this.approved });
      this.onSubmit();
    }
  }

  isFormValid(): boolean {
    this.headerService.clearAlerts();

    if (!this.quillService.quill.getText().trim()) {
      this.pushAlert('danger',
        `Release Notes can not be blank!`);
      return false;
    }

    if (this.notesForm.invalid) {
      this.notesForm.markAllAsTouched();
    }

    return !this.notesForm.invalid;
  }

  removeNote() {
    this.releaseNoteService.deleteReleaseNotes(this.noteID).subscribe(response => {
      if (response.isSuccess) {
        this.pushAlert('success',
          `Successfully deleted release notes version ${this.VersionNum.value} for ${this.datePipe.transform(response.data[0].releaseDate, 'MMM dd, yyyy', 'GMT')}!`);
      } else {
        this.pushAlert('danger',
          `Something went wrong: ${response.message}!`);
      }

      this.setupFormForEntry();
    });
  }

  postNewNote() {
    this.releaseNoteService.setReleaseNotes(this.notesForm.value).subscribe(response => {
      if (response.isSuccess) {
        this.pushAlert('success',
          `Successfully submitted release notes version ${this.VersionNum.value} for ${this.datePipe
            .transform(response.data[0].releaseDate, 'MMM dd, yyyy', 'GMT')}!`);
      } else {
        this.pushAlert('danger',
          `Something went wrong: ${response.message}!`);
      }

      this.setupFormForEntry();
    });
  }

  patchExistingNote() {
    this.releaseNoteService.updateReleaseNotes(this.notesForm.value).subscribe(response => {
      if (response.isSuccess) {
        this.pushAlert('success',
          `Successfully ${this.messageAction} release notes version ${this.VersionNum.value} for ${this.datePipe
            .transform(response.data[0].releaseDate, 'MMM dd, yyyy', 'GMT')}!`);
        this.messageAction = 'updated';
      } else {
        this.pushAlert('danger',
          `Something went wrong: ${response.message}!`);
      }
    });
  }

  pushAlert(typ: string, mes: string) {
    this.headerService.pushAlerts(typ, mes);
    window.scroll(0, 0);
  }

  sendEmail() {
    const date = new Date(this.RDate.value.year, this.RDate.value.month - 1, this.RDate.value.day, 0, 0, 0);
    const appName = this.appList.find(x => x.applicationId == this.ApplicationId.value).appName;
    const hotfix = this.formOriginal ? this.formOriginal.IsHotfix : this.IsHotfix.value;

    this.releaseNoteService.setTransferNote({ appName, releaseDate: date, versionNum: this.VersionNum.value, isHotfix: hotfix });

    this.router.navigate(['publishEmail/' + this.noteID]);
  }

  public ngOnDestroy() {
    if (this.xferNoteSub !== undefined) {
      this.xferNoteSub.unsubscribe();
    }
  }
}
